import { useMemo } from 'react';
import { TOLERANCE } from '../constants';

/**
 * Infers internal walls from room bounding boxes.
 * When two rooms share an edge (within TOLERANCE), that's an internal wall.
 *
 * Returns array of: { id, x1, y1, x2, y2, dir: 'h'|'v' }
 */
export default function useWallInference(rooms, extW, extH) {
  const walls = useMemo(() => {
    if (!rooms || rooms.length < 2) return [];

    const found = [];
    const wallSet = new Set(); // deduplicate

    for (let i = 0; i < rooms.length; i++) {
      const a = rooms[i];
      for (let j = i + 1; j < rooms.length; j++) {
        const b = rooms[j];

        // Check vertical shared edge (rooms side by side horizontally)
        // a's right edge ≈ b's left edge
        if (Math.abs((a.ax + a.aw) - b.ax) <= TOLERANCE) {
          const overlapTop = Math.max(a.ay, b.ay);
          const overlapBot = Math.min(a.ay + a.ah, b.ay + b.ah);
          if (overlapBot - overlapTop > TOLERANCE) {
            const key = `v-${Math.round(b.ax)}-${Math.round(overlapTop)}-${Math.round(overlapBot)}`;
            if (!wallSet.has(key)) {
              wallSet.add(key);
              found.push({
                id: `w-${i}-${j}-v`,
                x1: b.ax, y1: overlapTop,
                x2: b.ax, y2: overlapBot,
                dir: 'v',
              });
            }
          }
        }
        // b's right edge ≈ a's left edge
        if (Math.abs((b.ax + b.aw) - a.ax) <= TOLERANCE) {
          const overlapTop = Math.max(a.ay, b.ay);
          const overlapBot = Math.min(a.ay + a.ah, b.ay + b.ah);
          if (overlapBot - overlapTop > TOLERANCE) {
            const key = `v-${Math.round(a.ax)}-${Math.round(overlapTop)}-${Math.round(overlapBot)}`;
            if (!wallSet.has(key)) {
              wallSet.add(key);
              found.push({
                id: `w-${j}-${i}-v`,
                x1: a.ax, y1: overlapTop,
                x2: a.ax, y2: overlapBot,
                dir: 'v',
              });
            }
          }
        }

        // Check horizontal shared edge (rooms stacked vertically)
        // a's bottom edge ≈ b's top edge
        if (Math.abs((a.ay + a.ah) - b.ay) <= TOLERANCE) {
          const overlapLeft = Math.max(a.ax, b.ax);
          const overlapRight = Math.min(a.ax + a.aw, b.ax + b.aw);
          if (overlapRight - overlapLeft > TOLERANCE) {
            const key = `h-${Math.round(b.ay)}-${Math.round(overlapLeft)}-${Math.round(overlapRight)}`;
            if (!wallSet.has(key)) {
              wallSet.add(key);
              found.push({
                id: `w-${i}-${j}-h`,
                x1: overlapLeft, y1: b.ay,
                x2: overlapRight, y2: b.ay,
                dir: 'h',
              });
            }
          }
        }
        // b's bottom edge ≈ a's top edge
        if (Math.abs((b.ay + b.ah) - a.ay) <= TOLERANCE) {
          const overlapLeft = Math.max(a.ax, b.ax);
          const overlapRight = Math.min(a.ax + a.aw, b.ax + b.aw);
          if (overlapRight - overlapLeft > TOLERANCE) {
            const key = `h-${Math.round(a.ay)}-${Math.round(overlapLeft)}-${Math.round(overlapRight)}`;
            if (!wallSet.has(key)) {
              wallSet.add(key);
              found.push({
                id: `w-${j}-${i}-h`,
                x1: overlapLeft, y1: a.ay,
                x2: overlapRight, y2: a.ay,
                dir: 'h',
              });
            }
          }
        }
      }
    }

    return found;
  }, [rooms]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate external wall envelope from all rooms
  const envelope = useMemo(() => {
    if (!rooms || rooms.length === 0) return { x: 0, y: 0, w: extW, h: extH };
    const minX = Math.min(...rooms.map(r => r.ax));
    const minY = Math.min(...rooms.map(r => r.ay));
    const maxX = Math.max(...rooms.map(r => r.ax + r.aw));
    const maxY = Math.max(...rooms.map(r => r.ay + r.ah));
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }, [rooms, extW, extH]);

  return { walls, envelope };
}
